import { computed, effect, inject, Injector, linkedSignal, resource, Signal } from '@angular/core';

import { composeSchema, NO_DOCUMENT_LOADER } from '../reference/compose-schema';
import { SCHEMA_DOCUMENT_LOADER } from '../reference/schema-document-loader.token';
import { isSchemaLoading, schemaErrorOf, SchemaLoader, SchemaLoadState, schemaOf } from './schema-loader.types';

export interface SchemaLoaderConfig<TSchema, TCriteria> {
  /** `undefined` means "do not request" — that is how a loader defers its start. */
  criteria: Signal<TCriteria | undefined>;
  /**
   * Honouring `abortSignal` is optional and skipping it has a price: a request superseded by new
   * criteria, and a request outstanding when the injector is destroyed, both keep running to
   * completion — the loader only discards their outcome. `firstValueFrom(http.get(...))` never
   * unsubscribes by itself, so pipe it through `takeUntil(fromEvent(abortSignal, 'abort'))`.
   */
  load: (criteria: TCriteria, abortSignal: AbortSignal) => Promise<TSchema>;
  keepPreviousOnRefetch?: boolean;
  /** Present ⇒ criteria whose key is already cached issue no request at all. */
  cacheKey?: (criteria: TCriteria) => string;
  injector?: Injector;
}

/**
 * A root-schema load as signals. Every schema it loads is composed before it is cached or handed on (670357): each eager
 * reference in it becomes the document it names, loaded through the `SCHEMA_DOCUMENT_LOADER` reachable from
 * `config.injector`, or from where the loader is created. A part that fails to load fails the load, so the root shows
 * its error state, as for a page that did not arrive — and so does a reference where no app provides a loader. Parts of
 * a superseded load finish loading, and only their outcome is discarded, as with `load` itself.
 */
export function schemaLoader<TSchema, TCriteria>(config: SchemaLoaderConfig<TSchema, TCriteria>): SchemaLoader<TSchema> {
  const documentLoader =
    (config.injector ? config.injector.get(SCHEMA_DOCUMENT_LOADER, null) : inject(SCHEMA_DOCUMENT_LOADER, { optional: true })) ??
    NO_DOCUMENT_LOADER;

  // A plain Map, deliberately: writing to it must not invalidate `params`, or storing a freshly
  // loaded schema would immediately re-run the request that produced it. The skip takes effect on
  // the next criteria change, which is the only moment it matters.
  const cache = new Map<string, TSchema>();

  const keyOf = (criteria: TCriteria | undefined): string | undefined =>
    criteria === undefined || !config.cacheKey ? undefined : config.cacheKey(criteria);

  const params = computed(() => {
    const criteria = config.criteria();
    const key = keyOf(criteria);

    return key !== undefined && cache.has(key) ? undefined : criteria;
  });

  const schemaResource = resource({
    params,
    loader: async ({ params: criteria, abortSignal }) => {
      const schema = await composeSchema(await config.load(criteria, abortSignal), documentLoader, abortSignal);
      const key = keyOf(criteria);

      if (key !== undefined) {
        cache.set(key, schema);
      }

      return schema;
    },
    injector: config.injector,
  });

  /**
   * The last schema actually shown — from the resource or, on a cache hit, from the cache itself.
   * Mirrors what the office side panel did by hand before it moved here: keep the previous value while new criteria
   * load, and drop it on error or once criteria reset to `undefined` — a closed side panel must not
   * resurrect the schema it held before closing.
   *
   * A cache hit never runs the resource, so `schemaResource` alone would still report the schema
   * from whatever criteria last actually loaded — one navigation behind a cache-served schema.
   * Reading the cache here keeps `retained` in step with what `state`'s cache branch just displayed.
   *
   * `idle` keys on `config.criteria()`, not on the resource's own status: a cache hit routes
   * `params` to `undefined` while criteria stays defined, so the resource reports itself idle too —
   * keying on that would drop the retained schema on every cache hit instead of only on a real
   * criteria reset.
   */
  const retained = linkedSignal<{ schema: TSchema | undefined; failed: boolean; idle: boolean }, TSchema | undefined>({
    source: () => {
      const criteria = config.criteria();
      const key = keyOf(criteria);
      const cached = key === undefined ? undefined : cache.get(key);

      return {
        schema: cached ?? (schemaResource.hasValue() ? schemaResource.value() : undefined),
        failed: schemaResource.status() === 'error',
        idle: criteria === undefined,
      };
    },
    computation: ({ schema, failed, idle }, previous) => schema ?? (failed || idle ? undefined : previous?.value),
  });

  // `linkedSignal` is lazy the way a computed is: its `computation` runs only when something reads
  // it, so a schema nobody read while it was current never becomes `previous`. This effect is that
  // read, and it keeps `retained` live however rarely a consumer touches `state`.
  if (config.keepPreviousOnRefetch) {
    effect(() => retained(), { injector: config.injector });
  }

  const state = computed<SchemaLoadState<TSchema>>(() => {
    if (config.criteria() === undefined) {
      return { kind: 'idle' };
    }

    const cached = keyOf(config.criteria());

    if (cached !== undefined && cache.has(cached)) {
      return { kind: 'loaded', schema: cache.get(cached) as TSchema };
    }

    if (schemaResource.status() === 'error') {
      // `HttpErrorResponse` is error-like enough for `resource` to pass it through unwrapped, yet it
      // does not extend `Error` — `String()` on it yields `[object Object]`, which is every real HTTP
      // failure. Read `message` first and keep `String()` only for whatever was thrown without one.
      return { kind: 'error', error: schemaResource.error()?.message ?? String(schemaResource.error()) };
    }

    // An explicit `reload()` keeps the same request, so the resource preserves its stream and
    // `hasValue()` stays true throughout — `status()` is what actually flips to `'reloading'`.
    if (config.keepPreviousOnRefetch && (schemaResource.status() === 'reloading' || !schemaResource.hasValue())) {
      const previous = retained();

      if (previous !== undefined) {
        return { kind: 'reloading', schema: previous };
      }
    }

    // `hasValue()` is false while idle, loading and errored as long as no `defaultValue` is set —
    // which is why this factory never passes one. Reading `value()` outside this guard throws.
    return schemaResource.hasValue() ? { kind: 'loaded', schema: schemaResource.value() } : { kind: 'loading' };
  });

  return {
    state,
    schema: computed(() => schemaOf(state())),
    isLoading: computed(() => isSchemaLoading(state())),
    error: computed(() => schemaErrorOf(state())),
    reload: (): void => {
      schemaResource.reload();
    },
  };
}
